import { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';

import {
  AcademyAccessDeniedError,
  type AcademyArea,
  type AcademySkill,
  getTeacherAcademyDashboard,
} from '../Academy/AcademyService';
import { findSkillInArea } from '../../../components/academy/Utilities/academyView';
import {
  SkillDetail,
  type SkillProgressUpdate,
} from '../../../components/academy/SkillDetail/SkillDetail';
import { skillsetStyles } from './Skillset.styles';

type SkillsetState =
  | { status: 'loading' }
  | { status: 'ready'; skill: AcademySkill; planToken: string }
  | { status: 'error'; message: string };

interface SkillsetProps {
  initialSkillKey?: string;
}

export function Skillset({ initialSkillKey }: SkillsetProps) {
  const theme = useTheme();
  const [skillsetState, setSkillsetState] = useState<SkillsetState>({ status: 'loading' });

  useEffect(() => {
    let isCurrent = true;

    getTeacherAcademyDashboard()
      .then((dashboard) => {
        if (!isCurrent) {
          return;
        }

        const skillMatch = findSkillMatch(dashboard.academyAreas, initialSkillKey);

        if (!skillMatch) {
          setSkillsetState({
            status: 'error',
            message: 'Academy skill was not found.',
          });
          return;
        }

        setSkillsetState({
          status: 'ready',
          skill: skillMatch.skill,
          planToken: skillMatch.area.planToken,
        });
      })
      .catch((error: unknown) => {
        if (!isCurrent) {
          return;
        }

        if (error instanceof AcademyAccessDeniedError) {
          window.location.assign('/experience');
          return;
        }

        setSkillsetState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Academy request failed',
        });
      });

    return () => {
      isCurrent = false;
    };
  }, [initialSkillKey]);

  function handleProgressUpdate(skillKey: string, update: SkillProgressUpdate) {
    setSkillsetState((currentState) => {
      if (currentState.status !== 'ready' || currentState.skill.skillKey !== skillKey) {
        return currentState;
      }

      return {
        ...currentState,
        skill: {
          ...currentState.skill,
          ...update,
        },
      };
    });
  }

  if (skillsetState.status === 'loading') {
    return (
      <Box sx={skillsetStyles.shell(theme)}>
        <Typography sx={skillsetStyles.statePanel()} role="status">
          Loading Academy skill...
        </Typography>
      </Box>
    );
  }

  if (skillsetState.status === 'error') {
    return (
      <Box sx={skillsetStyles.shell(theme)}>
        <Typography sx={skillsetStyles.statePanel(true)} role="alert">
          {skillsetState.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={skillsetStyles.shell(theme)}>
      <Box sx={skillsetStyles.content()}>
        <Box
          component="header"
          sx={skillsetStyles.header()}
          aria-labelledby="skillset-page-title"
        >
          <Box sx={skillsetStyles.headerTitle()}>
            <Box component="span" sx={skillsetStyles.headerIcon()} aria-hidden="true">
              <AssignmentOutlinedIcon aria-hidden="true" focusable="false" fontSize="inherit" />
            </Box>
            <Typography
              id="skillset-page-title"
              component="h1"
              sx={skillsetStyles.title()}
            >
              {skillsetState.skill.title}
            </Typography>
          </Box>
        </Box>
        <Box sx={skillsetStyles.detail()}>
          <SkillDetail
            skill={skillsetState.skill}
            planToken={skillsetState.planToken}
            onProgressUpdate={handleProgressUpdate}
          />
        </Box>
      </Box>
    </Box>
  );
}

function findSkillMatch(
  academyAreas: AcademyArea[],
  skillKey: string | undefined,
): { area: AcademyArea; skill: AcademySkill } | undefined {
  if (!skillKey) {
    return undefined;
  }

  for (const area of academyAreas) {
    const skill = findSkillInArea(area, skillKey);

    if (skill) {
      return { area, skill };
    }
  }

  return undefined;
}
